import { Overlay, Icon } from '@eti/components';
import { Card, Inline, vars, Stack, Heading, Text } from '@etg/wings';
import { useTranslation, useSiteContext } from '@eti/providers';
import { css } from '@eti/styles';
import { ShieldCheck } from '@phosphor-icons/react';

const iconStyles = css`
  flex: 0 0 auto;
`;

const illustrationStyles = css`
  border-radius: 50%;
`;

const bulletListStyles = css`
  ul {
    list-style-type: disc;
    margin-block: 8px;
    padding-inline-start: 24px;
    width: 100%;

    li {
      padding-bottom: 8px;
    }

    li:last-child {
      padding-bottom: 0;
    }
  }
`;

interface TripItineraryDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const SelfTransferDealsDrawer = ({ isOpen, onClose }: TripItineraryDrawerProps) => {
  const { t } = useTranslation();
  const { brand } = useSiteContext();

  const cards = [...Array(4)].map((_, index) => {
    const num = index + 1;
    const title = t(`TravelerDetails.SelfTransferDeals.Drawer.Card${num}.Title`);
    const descriptionKey = `TravelerDetails.SelfTransferDeals.Drawer.Card${num}.Description`;
    const description = num === 3 ? t(descriptionKey, brand.code) : t(descriptionKey);

    let icon = null;
    if (num === 1) {
      icon = (
        <Icon
          aria-hidden
          className={illustrationStyles}
          color={vars.colors.promotional.foreground}
          data-testid="self-transfer-icon"
          id="self-transfer"
          size="20px"
        />
      );
    }
    if (num === 3) {
      icon = (
        <ShieldCheck
          aria-hidden
          className={iconStyles}
          color={vars.colors.signal.positive.foreground}
          size={20}
          weight="fill"
        />
      );
    }

    return { title, description, icon, isList: num === 2 };
  });

  return (
    <Overlay.Drawer
      dataTestId="trip-self-transfer-deals-overlay"
      handleClose={onClose}
      isOpen={isOpen}
    >
      <Overlay.Header
        dialogCloseButtonAriaLabel={t(
          'ScreenReader.TravelerDetails.SelfTransferDeals.Drawer.CloseButton.Label',
        )}
        onClose={onClose}
        title={t('TravelerDetails.SelfTransferDeals.Drawer.Title')}
      />
      <Overlay.Content data-testid="trip-self-transfer-deals-overlay-content">
        {cards.map(({ title, description, icon, isList }, index) => (
          <Card key={`${title}${index}`}>
            <Stack spacing={8}>
              <Inline noWrap spacing={8}>
                {icon}
                <Heading level={4}>{title}</Heading>
              </Inline>
              <Text asHtml className={isList ? bulletListStyles : ''}>
                {description}
              </Text>
            </Stack>
          </Card>
        ))}
      </Overlay.Content>
    </Overlay.Drawer>
  );
};

export default SelfTransferDealsDrawer;
